import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Coins, Lock, Sparkles, TreeDeciduous, 
  Store, Lightbulb, Trophy, Calendar, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const CoinWorldPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [coinWorld, setCoinWorld] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoinWorld();
  }, []);

  const fetchCoinWorld = async () => {
    try {
      const response = await api.get('/coin-world');
      setCoinWorld(response.data);
      
      // Show ready rewards notification
      if (response.data.ready_rewards?.length > 0) {
        const totalReward = response.data.ready_rewards.reduce((sum, r) => sum + r.reward_amount, 0);
        toast.success(`Your patience paid off! You received ${totalReward} coins! 🎉`);
        await refreshUser();
      }
      
      // Show growth notification
      if (response.data.growth_applied > 0) {
        toast.success(`Your Save Jar grew by ${response.data.growth_applied} coins! 🌱`);
      }
    } catch (error) {
      console.error('Failed to fetch coin world:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">🪙</div>
          <p className="text-xl text-slate-600">Loading Coin World...</p>
        </div>
      </div>
    );
  }

  // Not unlocked yet
  if (!coinWorld?.unlocked) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="coin-world-locked">
        <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <h1 className="text-xl font-bold text-slate-900 font-fredoka ml-3">Coin World</h1>
          </div>
        </header>
        
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-24 h-24 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-6">
              <Lock className="w-12 h-12 text-slate-400" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 font-fredoka mb-3">
              Coin World is Locked!
            </h2>
            <p className="text-lg text-slate-600 mb-6">
              {coinWorld?.message || "Complete your daily Brain Gym Jr. session to unlock Coin World!"}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="go-to-session-btn"
            >
              <Sparkles className="w-5 h-5" />
              Start Brain Gym Jr.
            </button>
          </div>
        </main>
      </div>
    );
  }

  const menuItems = [
    {
      id: 'vault',
      title: 'My Coin Vault',
      description: 'Save coins in magical jars',
      emoji: '🏦',
      color: 'bg-amber-100',
      path: '/coin-world/vault',
      stats: `${(coinWorld.vault?.spend_jar || 0) + (coinWorld.vault?.save_jar || 0) + (coinWorld.vault?.dream_jar || 0)} coins stored`
    },
    {
      id: 'garden',
      title: 'Coin Garden',
      description: 'Plant coins and watch them grow',
      emoji: '🌱',
      color: 'bg-green-100',
      path: '/coin-world/garden',
      stats: coinWorld.garden?.harvestable_count > 0 
        ? `${coinWorld.garden.harvestable_count} ready to harvest!` 
        : 'Plant your first seed!'
    },
    {
      id: 'market',
      title: 'Mini Market',
      description: 'Buy cool stuff with coins',
      emoji: '🏪',
      color: 'bg-blue-100',
      path: '/coin-world/market',
      stats: 'New items every day!'
    },
    {
      id: 'collections',
      title: 'Pet World',
      description: 'Adopt, feed & grow cute pets!',
      emoji: '🐾',
      color: 'bg-pink-100',
      path: '/coin-world/pets',
      stats: 'Adopt your first pet!'
    },
    {
      id: 'summary',
      title: 'Coin Bank Day',
      description: 'Weekly progress report',
      emoji: '📊',
      color: 'bg-orange-100',
      path: '/coin-world/summary',
      stats: 'See your growth!'
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="coin-world-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-fredoka">Coin World</h1>
              <p className="text-sm text-slate-500">Learn to save, invest & spend wisely!</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full" data-testid="coin-display">
            <Coins className="w-5 h-5 text-primary" />
            <span className="font-bold text-slate-900">{coinWorld.coins || 0}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Banner */}
        <div className="card-bubbly bg-gradient-to-br from-primary/10 to-secondary/10">
          <div className="flex items-center gap-4">
            <div className="text-5xl">🪙</div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-fredoka">
                Welcome to Coin World!
              </h2>
              <p className="text-slate-600">
                Learn money skills while having fun. No real money needed!
              </p>
            </div>
          </div>
        </div>

        {/* Save Streak */}
        {coinWorld.save_streak > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
            <span className="text-3xl">🔥</span>
            <div>
              <p className="font-bold text-green-800">
                {coinWorld.save_streak} Day Save Streak!
              </p>
              <p className="text-sm text-green-600">
                {coinWorld.save_streak >= 3 
                  ? "Your Save Jar is growing 5% every 3 days!" 
                  : `Save ${3 - (coinWorld.save_streak % 3)} more days for bonus growth!`}
              </p>
            </div>
          </div>
        )}

        {/* Menu Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`card-bubbly ${item.color} text-left hover-bounce tap-shrink`}
              data-testid={`menu-${item.id}`}
            >
              <div className="text-4xl mb-3">{item.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
              <p className="text-sm text-slate-600 mb-2">{item.description}</p>
              <div className="flex items-center gap-1 text-xs font-medium text-secondary">
                <span>{item.stats}</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>

        {/* Active Booster */}
        {coinWorld.active_booster && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex items-center gap-3">
            <span className="text-3xl">🍀</span>
            <div>
              <p className="font-bold text-yellow-800">Lucky Clover Active!</p>
              <p className="text-sm text-yellow-600">+5 bonus coins on your next session!</p>
            </div>
          </div>
        )}

        {/* Safety Note */}
        <div className="text-center text-sm text-slate-400 py-4">
          <p>🔒 Coin World uses pretend coins only. No real money!</p>
        </div>
      </main>
    </div>
  );
};

export default CoinWorldPage;
